// Kindra Health · Concept D · REAL PRODUCT TRUTH RESET · PREVIEW ONLY
//
// Founder direction (verbatim): "The public site must not sell fake
// screenshots, fake product surfaces, or idealised UI. Build the
// homepage around the REAL Kindra product experience."
//
// Every product image on this page is a real screenshot of the live
// app, captured by `/app/scripts/capture_real_app_screenshots.py`
// against the seeded demo account. Files live in
// `/app/frontend/public/real-app/`. No fake dashboards, no mock
// product shells, no idealised UI. If the image isn't a real
// screenshot, it isn't on this page.

import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../services/activation';
import {
  PAPER, NAVY, NAVY_MUTED, NAVY_SOFT, TEAL, TEAL_SOFT, HAIRLINE, SAGE_BG,
  ArrowRight, CheckTick,
  ConceptFooter,
} from './_shared';

// ── Real screenshots (live app) ───────────────────────────
const SHOT = {
  chat:           '/real-app/chat-desktop.png',
  chatMobile:     '/real-app/chat-mobile.png',
  chatZoom:       '/real-app/chat-zoom.png',           // Kindra reply + context chips · readable crop
  home:           '/real-app/home-desktop.png',
  homeMobile:     '/real-app/home-mobile.png',
  homeZoom:       '/real-app/home-zoom.png',           // Today's one thing · readable crop
  homeChanged:    '/real-app/home-changed-zoom.png',   // What changed · readable crop
  documents:      '/real-app/documents-desktop.png',
  documentsZoom:  '/real-app/documents-zoom.png',
  appointments:   '/real-app/appointments-desktop.png',
  appointmentsZoom: '/real-app/appointments-zoom.png',
  health:         '/real-app/health-desktop.png',
  healthZoom:     '/real-app/health-zoom.png',
  prepare:        '/real-app/prepare-desktop.png',
  prepareZoom:    '/real-app/prepare-zoom.png',
};

// ─── Top nav · explicit sections, no hidden footer-only nav ──
const NAV_LINKS = [
  { label: 'How it works',     to: 'how-it-works'     },
  { label: 'Ask Kindra',       to: 'ask'              },
  { label: 'Reports',          to: 'reports'          },
  { label: 'Appointment prep', to: 'appointment-prep' },
  { label: 'Trust',            to: 'trust'            },
  { label: 'Pricing',          to: 'pricing'          },
];

const TopNavD = ({ onStart, onLogin, user }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileOpen(false);
  };
  return (
    <nav
      className="w-full sticky top-0 z-40 safe-top"
      style={{ backgroundColor: PAPER, borderBottom: `1px solid ${HAIRLINE}` }}
      data-testid="d-nav"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-16 h-16 md:h-[72px] flex items-center justify-between gap-4 md:gap-6">
        <Link to="/concept-d" className="inline-flex items-center gap-2 flex-shrink-0 min-h-[44px]" aria-label="Kindra home">
          <img src="/kindra-logo.png" alt="Kindra" className="h-9 md:h-10 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <button
              key={l.to}
              onClick={() => scrollTo(l.to)}
              className="px-3 py-2 text-[13.5px] font-medium font-sans rounded-full transition-opacity hover:opacity-70"
              style={{ color: NAVY }}
              data-testid={`d-nav-${l.to}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 md:gap-3">
          {/* Mobile/tablet · "Explore" toggle reveals the 6 nav links */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-2 min-h-[44px] rounded-full transition-opacity hover:opacity-70"
            style={{ color: NAVY, border: `1px solid ${HAIRLINE}` }}
            aria-expanded={mobileOpen}
            aria-controls="d-nav-mobile-menu"
            data-testid="d-nav-mobile-toggle"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d={mobileOpen ? 'M6 6l12 12M18 6L6 18' : 'M3 7h18M3 12h18M3 17h18'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>{mobileOpen ? 'Close' : 'Explore'}</span>
          </button>

          {user ? (
            <button onClick={() => (window.location.href = '/home')} className="text-[13.5px] font-medium px-3 md:px-4 py-2 min-h-[44px] rounded-full" style={{ color: NAVY }} data-testid="d-nav-continue">
              Continue
            </button>
          ) : (
            <>
              <button onClick={onLogin} className="inline-flex items-center text-[13.5px] font-medium px-3 py-2 min-h-[44px] transition-opacity hover:opacity-70" style={{ color: NAVY }} data-testid="d-nav-signin">
                Sign in
              </button>
              <button onClick={onStart} className="inline-flex items-center gap-1.5 rounded-full text-[13.5px] font-medium px-4 md:px-5 py-2 min-h-[44px] transition-colors" style={{ backgroundColor: NAVY, color: PAPER }} data-testid="d-nav-cta">
                Try free
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile/tablet drawer */}
      {mobileOpen && (
        <div
          id="d-nav-mobile-menu"
          className="lg:hidden border-t"
          style={{ backgroundColor: PAPER, borderColor: HAIRLINE }}
          data-testid="d-nav-mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-2 flex flex-col">
            {NAV_LINKS.map((l) => (
              <button
                key={l.to}
                onClick={() => scrollTo(l.to)}
                className="text-left px-3 py-3 text-[15px] font-medium font-sans rounded-lg transition-colors hover:bg-[#F4F0E6]"
                style={{ color: NAVY, borderBottom: `1px solid ${HAIRLINE}` }}
                data-testid={`d-nav-mobile-${l.to}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

// ─── 1 · HERO · real Ask Kindra screenshot is the proof ──
// In-hero "Live Moment" sub-component — split card showing one real
// captured Kindra response from /app/scripts/_concept_d_real_responses.json
// (specialist-prep · the strongest healthcare-companion first
// impression). Verbatim from the live model output, shortened for hero
// space. Subtle fade-in on mount.
const HeroLiveMoment = () => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShown(true), 60);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-5 transition-all duration-700 ease-out"
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(8px)',
      }}
      data-testid="d-hero-live"
    >
      {/* LEFT · real question */}
      <aside
        className="lg:col-span-5 rounded-2xl p-5 md:p-7"
        style={{ backgroundColor: SAGE_BG, border: `1px solid ${HAIRLINE}` }}
        data-testid="d-hero-live-question"
      >
        <p className="font-sans text-[10.5px] uppercase font-medium mb-2.5" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>
          Real question
        </p>
        <p className="font-serif text-[19px] md:text-[24px] leading-[1.35]" style={{ color: NAVY }}>
          "I'm seeing a specialist tomorrow and don't know where to start."
        </p>
        <p className="font-sans mt-3 md:mt-4 text-[12.5px]" style={{ color: NAVY_SOFT }}>
          The kind of question Kindra is built for.
        </p>
      </aside>

      {/* RIGHT · real Kindra response (verbatim, shortened to 2 strongest paragraphs for hero) */}
      <article
        className="lg:col-span-7 rounded-2xl p-5 md:p-7"
        style={{ backgroundColor: NAVY, color: PAPER, border: '1px solid rgba(251,250,246,0.08)' }}
        data-testid="d-hero-live-response"
      >
        <div className="flex items-center gap-2.5 mb-3 md:mb-4">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ backgroundColor: TEAL_SOFT }}>
            <CheckTick size={12} color={TEAL} />
          </span>
          <span className="font-sans text-[10.5px] uppercase font-medium" style={{ color: TEAL, letterSpacing: '0.22em' }}>
            Kindra · real response
          </span>
        </div>
        <p className="font-serif text-[17px] md:text-[20px] leading-[1.45]" style={{ color: PAPER }}>
          Start with a 30-second headline. If you can give them that cleanly, the rest of the appointment usually clicks into place.
        </p>
        <p className="mt-3 md:mt-4 font-sans text-[13.5px] md:text-[14.5px] leading-[1.6]" style={{ color: 'rgba(251,250,246,0.85)' }}>
          Given you've mentioned fatigue getting worse over the last 3 weeks and headaches a few afternoons a week, I'd put those near the top — along with the fact you started Sertraline around 3 weeks ago — because that timing may matter, even if it ends up being unrelated.
        </p>
        <p className="mt-3 md:mt-4 font-sans text-[13.5px] md:text-[14.5px] leading-[1.6]" style={{ color: 'rgba(251,250,246,0.85)' }}>
          Tell me the specialist and your main problem in one sentence — I'll turn it into a clear opening and the right questions.
        </p>
      </article>

      {/* Caption strip below — spans both columns */}
      <p
        className="lg:col-span-12 mt-0.5 font-sans italic text-[12px] md:text-[12.5px]"
        style={{ color: NAVY_SOFT }}
        data-testid="d-hero-live-caption"
      >
        Real response from Kindra. Answers adapt to your health record.
      </p>
    </div>
  );
};

const HeroD = ({ onStart, onSeeExamples }) => (
  <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }} data-testid="d-hero">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-10 md:pt-20 lg:pt-24 pb-12 md:pb-20">
      <div className="max-w-[920px]">
        <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4 md:mb-5" style={{ color: TEAL, letterSpacing: '0.26em' }} data-testid="d-hero-eyebrow">
          A health companion · built around your context
        </p>
        <h1
          className="font-serif leading-[1.0] tracking-[-0.022em] font-normal text-[40px] sm:text-[56px] lg:text-[70px] xl:text-[78px]"
          style={{ color: NAVY }}
          data-testid="d-hero-headline"
        >
          Health support that <em className="italic" style={{ color: TEAL }}>remembers your context.</em>
        </h1>
        <p
          className="font-sans mt-6 md:mt-10 text-[15.5px] md:text-[19px] leading-[1.55] max-w-[760px]"
          style={{ color: NAVY_MUTED }}
          data-testid="d-hero-subhead"
        >
          Kindra remembers your reports, medications, symptoms and appointments — so you can ask better questions, understand what matters, and prepare for care.
        </p>
      </div>

      {/* NEW · Live Moment · the 3-second emotional recognition block */}
      <HeroLiveMoment />

      <div className="max-w-[920px]">
        <div className="mt-7 md:mt-9 flex flex-wrap items-center gap-4 md:gap-5">
          <button
            onClick={onStart}
            className="inline-flex items-center gap-2 rounded-full px-9 py-5 text-[16px] md:text-[17px] font-medium font-sans transition-all hover:translate-y-[-1px]"
            style={{ backgroundColor: NAVY, color: PAPER, boxShadow: '0 14px 34px -14px rgba(14,28,46,0.4)' }}
            data-testid="d-hero-cta-start"
          >
            Start free
            <ArrowRight size={16} stroke={2} />
          </button>
          <button
            onClick={onSeeExamples}
            className="inline-flex items-center gap-1.5 py-3 text-[14px] md:text-[15px] font-medium transition-opacity hover:opacity-70"
            style={{ color: NAVY, borderBottom: `1px solid ${NAVY}` }}
            data-testid="d-hero-cta-examples"
          >
            See how Kindra thinks
            <ArrowRight size={13} stroke={2} />
          </button>
          <span className="font-sans text-[12.5px]" style={{ color: NAVY_SOFT }}>
            30 days free · no card · private by design
          </span>
        </div>
      </div>

      {/* Real /chat zoom · readable crop of the Kindra reply + context chips */}
      <figure
        className="mt-8 md:mt-10 rounded-2xl overflow-hidden"
        style={{ border: `1px solid ${HAIRLINE}`, boxShadow: '0 50px 100px -40px rgba(14,28,46,0.32)' }}
        data-testid="d-hero-shot"
      >
        <img
          src={SHOT.chatZoom}
          alt="Real Kindra reply (cropped from the live Ask page) preparing the user for a specialist visit, with 'BASED ON YOUR RECORD' context chips below the response — Hashimoto's thyroiditis and Iron deficiency, Levothyroxine, Iron polymaltose, the endocrinology review — followed by action buttons (Prepare this for my doctor, Save as one-pager, Explain simpler, Show sources, Ask follow-up, Draft GP email, Save to notes) and the disclaimer 'Kindra can help explain and prepare — it doesn't diagnose, prescribe, or replace your clinician.'"
          className="block w-full"
          loading="eager"
        />
        <figcaption
          className="px-5 md:px-6 py-3 font-sans text-[12px] md:text-[12.5px] italic"
          style={{ backgroundColor: SAGE_BG, color: NAVY_SOFT, borderTop: `1px solid ${HAIRLINE}` }}
        >
          Real screenshot from the live Kindra app. Kindra is using the saved context — Hashimoto's, Levothyroxine, the endocrinology review — to shape the answer.
        </figcaption>
      </figure>
    </div>
  </section>
);

// ─── 2 · HOW KINDRA HELPS · plain-English, no fake UI ────
const HowItHelpsD = () => {
  const helps = [
    {
      title: 'Remembers what matters',
      body: 'Conditions, medications, recent symptoms, reports, appointments — once they\'re in Kindra, they\'re used every time you ask a question.',
    },
    {
      title: 'Answers in your context',
      body: 'You can ask the messy real-life questions you\'d normally ask Google. Kindra answers with your own situation in mind, not generic internet advice.',
    },
    {
      title: 'Helps you prepare for care',
      body: 'When you have an appointment coming up, Kindra helps you turn what\'s in your record into a clear opening, the right questions, and the things worth raising.',
    },
    {
      title: 'Stays on your side',
      body: 'Never diagnoses, never prescribes, never replaces your clinician. It supports the conversation between visits.',
    },
  ];
  return (
    <section
      id="how-it-works"
      className="relative scroll-mt-20"
      style={{ backgroundColor: SAGE_BG, borderTop: `1px solid ${HAIRLINE}` }}
      data-testid="d-how"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="max-w-[760px] mb-12 md:mb-14">
          <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>
            How Kindra helps
          </p>
          <h2 className="font-serif text-[34px] md:text-[48px] lg:text-[56px] leading-[1.06] tracking-[-0.018em] font-normal" style={{ color: NAVY }}>
            Quiet, useful work — <em className="italic">between appointments.</em>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {helps.map((h, i) => (
            <article
              key={i}
              className="rounded-2xl p-7 md:p-8"
              style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
              data-testid={`d-how-${i}`}
            >
              <p className="font-sans text-[10.5px] uppercase font-medium mb-3" style={{ color: TEAL, letterSpacing: '0.22em' }}>0{i + 1}</p>
              <p className="font-serif text-[22px] md:text-[26px] leading-tight mb-3" style={{ color: NAVY }}>{h.title}</p>
              <p className="font-sans text-[15px] leading-[1.6]" style={{ color: NAVY_MUTED }}>{h.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Reusable real-screenshot showcase block ─────────────
// Renders the full-page real screenshot AND a separate readable-detail
// callout. On desktop the callout is its OWN column beside the figure
// (no longer floating over it). On mobile the 3-column grid collapses
// and the callout naturally stacks below the figure. `zoomAlign` lets
// each section anchor the callout to the part of the screenshot it
// refers to (start = top, center = middle, end = bottom).
//
// Hierarchy rules locked in (cognitive clarity pass):
//   · The full screenshot is desaturated so the eye lands on the zoom
//   · "What to notice" sits ABOVE the zoom inside the callout column
//   · The zoom column is slightly narrower than its grid track so it
//     reads as guided detail, not a competing panel
const RealAppShot = ({ id, eyebrow, title, body, doing, why, image, imageAlt, zoom, zoomAlt, notice, swap, zoomAlign = 'center', testId }) => {
  const alignClass = {
    start: 'lg:self-start',
    center: 'lg:self-center',
    end: 'lg:self-end',
  }[zoomAlign] || 'lg:self-center';
  return (
    <section
      id={id}
      className="relative scroll-mt-20"
      style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
      data-testid={testId}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 items-start ${swap ? 'lg:[direction:rtl]' : ''}`}>
          {/* Text column */}
          <div className="lg:col-span-4 lg:[direction:ltr]">
            <p className="font-sans text-[11px] uppercase font-medium mb-3" style={{ color: TEAL, letterSpacing: '0.24em' }}>{eyebrow}</p>
            <h2 className="font-serif text-[28px] md:text-[40px] lg:text-[44px] leading-[1.07] tracking-[-0.014em] font-normal mb-5" style={{ color: NAVY }}>
              {title}
            </h2>
            <p className="font-sans text-[15px] md:text-[16px] leading-[1.6] mb-7" style={{ color: NAVY_MUTED }}>
              {body}
            </p>
            <dl className="space-y-4">
              <div>
                <dt className="font-sans text-[10.5px] uppercase font-medium mb-1" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>What you do</dt>
                <dd className="font-sans text-[14px] leading-[1.6]" style={{ color: NAVY }}>{doing}</dd>
              </div>
              <div>
                <dt className="font-sans text-[10.5px] uppercase font-medium mb-1" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>Why it matters</dt>
                <dd className="font-sans text-[14px] leading-[1.6]" style={{ color: NAVY }}>{why}</dd>
              </div>
            </dl>
          </div>

          {/* Full screenshot · own column · DESATURATED so eye lands on zoom callout */}
          <figure
            className="lg:col-span-5 lg:[direction:ltr] rounded-2xl overflow-hidden"
            style={{ border: `1px solid ${HAIRLINE}`, boxShadow: '0 40px 80px -36px rgba(14,28,46,0.18)' }}
          >
            <img
              src={image}
              alt={imageAlt}
              className="block w-full"
              style={{ filter: 'saturate(0.7) opacity(0.92)' }}
              loading="lazy"
            />
            <figcaption
              className="px-5 py-2.5 font-sans text-[11.5px] italic"
              style={{ backgroundColor: SAGE_BG, color: NAVY_SOFT, borderTop: `1px solid ${HAIRLINE}` }}
            >
              Real screenshot · live Kindra app
            </figcaption>
          </figure>

          {/* Readable-detail column · holds "What to notice" ABOVE the zoom image.
              Slightly narrower than its grid track on desktop so it feels like
              guided detail, not a competing panel. */}
          {zoom && (
            <div
              className={`lg:col-span-3 lg:[direction:ltr] lg:w-[88%] ${alignClass}`}
              data-testid={`${testId}-zoom-col`}
            >
              {notice && (
                <p
                  className="mb-3 lg:mb-3.5 font-sans text-[13px] md:text-[13.5px] leading-[1.55]"
                  style={{ color: NAVY_MUTED }}
                  data-testid={`${testId}-notice`}
                >
                  <span className="font-medium" style={{ color: NAVY }}>What to notice · </span>
                  {notice}
                </p>
              )}
              <aside
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}`, boxShadow: '0 22px 44px -18px rgba(14,28,46,0.22)' }}
                data-testid={`${testId}-zoom`}
              >
                <div className="px-3 py-1.5 font-sans text-[10px] uppercase font-medium flex items-center gap-2" style={{ color: TEAL, letterSpacing: '0.22em', backgroundColor: SAGE_BG, borderBottom: `1px solid ${HAIRLINE}` }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: TEAL }} />
                  Readable detail
                </div>
                <img src={zoom} alt={zoomAlt || imageAlt} className="block w-full" loading="lazy" />
              </aside>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ─── 3 · REAL APP WALKTHROUGH ────────────────────────────
// Top: a single banner introducing the walkthrough · then five
// real-screenshot showcase blocks for each surface.
const WalkthroughIntro = () => (
  <section
    id="ask"
    className="relative scroll-mt-20"
    style={{ backgroundColor: NAVY, color: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
    data-testid="d-walk-intro"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24">
      <div className="max-w-[820px]">
        <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>
          A real walkthrough · live screenshots
        </p>
        <h2 className="font-serif text-[34px] md:text-[48px] lg:text-[56px] leading-[1.06] tracking-[-0.018em] font-normal">
          What Kindra actually <em className="italic" style={{ color: TEAL }}>looks like inside.</em>
        </h2>
        <p className="font-sans mt-6 md:mt-8 text-[16px] md:text-[17.5px] leading-[1.6] max-w-[680px]" style={{ color: 'rgba(251,250,246,0.78)' }}>
          The screenshots below come straight from the live app — same pages you'll use after signup. No mockups, no idealised dashboards, no UI we don't ship today.
        </p>
        <p
          className="font-sans mt-4 text-[15px] md:text-[16px] leading-[1.55] max-w-[640px]"
          style={{ color: 'rgba(251,250,246,0.65)' }}
          data-testid="d-walk-continuity"
        >
          These are all parts of the same system — your record connects everything.
        </p>
      </div>
    </div>
  </section>
);

// ─── 7 · TRUST ───────────────────────────────────────────
const TrustD = () => {
  const pillars = [
    'Private by design',
    'Never sold',
    'Never prescribes or diagnoses',
    'Supports care, never replaces clinicians',
    'Built in Australia',
  ];
  return (
    <section
      id="trust"
      className="relative scroll-mt-20"
      style={{ backgroundColor: SAGE_BG, borderTop: `1px solid ${HAIRLINE}` }}
      data-testid="d-trust"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24 text-center">
        <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>Built for trust</p>
        <h2 className="font-serif text-[34px] md:text-[48px] leading-[1.08] tracking-[-0.012em] font-normal" style={{ color: NAVY }}>
          Health information deserves <em className="italic">extra care.</em>
        </h2>
        <div className="mt-10 md:mt-12 flex flex-wrap items-center justify-center gap-2.5 md:gap-3">
          {pillars.map((l, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-2 rounded-full pl-3.5 pr-5 py-2.5"
              style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
              data-testid={`d-trust-${i}`}
            >
              <CheckTick size={13} />
              <span className="font-sans text-[13px]" style={{ color: NAVY }}>{l}</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── 8 · TRIAL CTA ───────────────────────────────────────
const TrialCTAD = ({ onStart }) => (
  <section
    id="pricing"
    className="relative scroll-mt-20"
    style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
    data-testid="d-cta"
  >
    <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 text-center">
      <h2 className="font-serif text-[44px] md:text-[64px] lg:text-[76px] leading-[1.04] tracking-[-0.02em] font-normal max-w-[900px] mx-auto" style={{ color: NAVY }} data-testid="d-cta-headline">
        30 days free. <em className="italic">No card needed.</em>
      </h2>
      <p className="font-sans mt-6 md:mt-9 text-[17px] md:text-[20px] leading-[1.5] max-w-[640px] mx-auto" style={{ color: NAVY_MUTED }}>
        See what healthcare feels like when someone finally holds your whole context.
      </p>
      <p className="font-sans mt-4 text-[13.5px]" style={{ color: NAVY_SOFT }}>
        A$9.99/month after · cancel anytime in one click.
      </p>
      <button
        onClick={onStart}
        className="mt-10 md:mt-12 inline-flex items-center gap-2 rounded-full px-10 py-5 text-[16px] md:text-[17px] font-medium font-sans transition-colors"
        style={{ backgroundColor: NAVY, color: PAPER, boxShadow: '0 14px 34px -14px rgba(14,28,46,0.4)' }}
        data-testid="d-cta-button"
      >
        Start free
        <ArrowRight size={16} stroke={2} />
      </button>
      <p className="mt-6 font-sans text-[12.5px]" style={{ color: NAVY_SOFT }}>
        Kindra is a health companion — it explains and remembers. It doesn't diagnose, prescribe or replace your clinician.
      </p>
    </div>
  </section>
);

// ─── Page composition ────────────────────────────────────
const ConceptDPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    try { track('concept_viewed', { concept: 'D' }); } catch { /* noop */ }
  }, []);
  const goStart = () => { try { track('clicked_start_free', { src: 'concept_d' }); } catch {} navigate('/register'); };
  const goLogin = () => navigate('/login');
  const goSeeExamples = () => {
    if (typeof document !== 'undefined') {
      const el = document.getElementById('ask');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  return (
    <div className="min-h-screen font-sans" style={{ backgroundColor: PAPER, color: NAVY }} data-testid="concept-d-page">
      <TopNavD onStart={goStart} onLogin={goLogin} user={user} />
      <HeroD onStart={goStart} onSeeExamples={goSeeExamples} />
      <HowItHelpsD />
      <WalkthroughIntro />

      {/* Real walkthrough · 6 surfaces · each with full screenshot + readable zoom + "what to notice" line */}
      <RealAppShot
        eyebrow="Surface 01 · Ask"
        title="Ask the messy real-life question."
        body={"Type or speak the question you'd normally ask Google. Kindra reads what's already in your record — conditions, medications, symptoms, recent reports — and answers in your context."}
        doing="Ask your question. Kindra answers using your saved context — and shows what it used."
        why="Generic search doesn't know your context. Kindra does."
        image={SHOT.chat}
        imageAlt="Live Kindra Ask page showing a real conversation about preparing for a specialist visit, with context chips for Hashimoto's thyroiditis, Iron deficiency, Levothyroxine, Iron polymaltose, and the upcoming endocrinology review."
        zoom={SHOT.chatZoom}
        zoomAlt="Readable crop of the Kindra reply with the BASED ON YOUR RECORD context chips visible — Hashimoto's, Levothyroxine, Iron polymaltose, the endocrinology review."
        zoomAlign="center"
        notice="Kindra is connecting your symptoms, medications and timing — so the answer fits your situation, not the internet."
        testId="d-walk-ask"
      />

      <RealAppShot
        id="reports"
        eyebrow="Surface 02 · Reports"
        title="Your reports, in one place."
        body={"Upload pathology, imaging or letters. They live in Reports, ready to be referenced when you ask Kindra a question or prepare for an appointment."}
        doing="Drop in a PDF or photo. Kindra extracts what matters into your record."
        why="The next time you ask, the report is already there to ground the answer."
        image={SHOT.documents}
        imageAlt="Live Kindra Reports page showing the user's uploaded documents."
        zoom={SHOT.documentsZoom}
        zoomAlt="Readable crop of the Reports page showing the upload area and how reports become reusable context."
        zoomAlign="start"
        notice="Reports become reusable context — what you upload once is used in every future answer."
        swap
        testId="d-walk-reports"
      />

      <RealAppShot
        id="appointment-prep"
        eyebrow="Surface 03 · Appointment prep"
        title="Walk in already understood."
        body={"When you have a visit coming up, open Appointment prep. Kindra takes what it knows about you and helps you prepare a short, editable summary you can bring."}
        doing="Open Prepare, review the drafted summary, edit anything, and bring it to the visit."
        why="The visit moves faster when the clinician sees the whole picture, not the slice."
        image={SHOT.prepare}
        imageAlt="Live Kindra Appointment prep page showing a Health Summary for Doctor with conditions, current medications and recent trends."
        zoom={SHOT.prepareZoom}
        zoomAlt="Readable crop of the Appointment prep page showing the generated health summary."
        zoomAlign="start"
        notice="Kindra turns your record into a clear opening and the right questions — before you walk in."
        testId="d-walk-prep"
      />

      <RealAppShot
        eyebrow="Surface 04 · Appointments"
        title="Every visit, on one timeline."
        body={"Upcoming visits, past visits, who you saw, what they said. The Appointments view holds the rhythm of your care so nothing gets lost between visits."}
        doing="Add appointments when booked. After each visit, log what changed and what's next."
        why="When 'what did the specialist say last time?' comes up, the answer is right there."
        image={SHOT.appointments}
        imageAlt="Live Kindra Appointments page showing one upcoming endocrinology review and several past visits with notes."
        zoom={SHOT.appointmentsZoom}
        zoomAlt="Readable crop of the Appointments page showing the next appointment and prep summary."
        zoomAlign="start"
        notice="Each visit builds on the last — nothing gets lost between appointments."
        swap
        testId="d-walk-appts"
      />

      <RealAppShot
        eyebrow="Surface 05 · Record"
        title="This is what Kindra knows about you."
        body={"Conditions, medications, allergies, the small notes you've asked Kindra to remember. Open, editable, yours."}
        doing="Browse and edit your record — conditions, medications, allergies, and the notes that shape Kindra's answers."
        why="When the record fits your real life, the answers do too."
        image={SHOT.health}
        imageAlt="Live Kindra Record page showing the Snapshot tab with conditions, medications, allergies and recent activity."
        zoom={SHOT.healthZoom}
        zoomAlt="Readable crop of the Record page showing notes Kindra is monitoring and recent context."
        zoomAlign="center"
        notice="What you tell Kindra once carries forward — shaping every future answer."
        testId="d-walk-record"
      />

      <RealAppShot
        eyebrow="Surface 06 · Home"
        title="A quiet daily check-in."
        body={"One small thing worth doing today, the things you've already set up, and a glance at what's changed lately."}
        doing="Open Kindra. Home is the first thing you see — calm, short, yours to act on or skip."
        why="Some days don't need an app — they need one quiet line."
        image={SHOT.home}
        imageAlt="Live Kindra Home page showing 'Today's one thing', the user's setup checklist, and a 'What changed' panel."
        zoom={SHOT.homeChanged}
        zoomAlt="Readable crop of the Home page's What-Changed panel."
        zoomAlign="center"
        notice="Kindra surfaces what changed across your record — so you don't have to piece it together."
        swap
        testId="d-walk-home"
      />

      <TrustD />
      <TrialCTAD onStart={goStart} />
      <ConceptFooter />
    </div>
  );
};

export default ConceptDPage;
